"use client";

import { Check, Globe } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { usePathname } from "@/i18n/navigation";
import { LOCALE_COOKIE, localeMeta, routing, type Locale } from "@/i18n/routing";

export function LanguageToggle() {
  const t = useTranslations("header");
  const currentLocale = useLocale() as Locale;
  const pathname = usePathname();

  function setLocale(next: Locale) {
    if (next === currentLocale) return;
    // Manual choice — wins over IP geolocation on subsequent visits.
    document.cookie = `${LOCALE_COOKIE}=${next}; path=/; max-age=31536000; samesite=lax`;
    // Hard navigation instead of next-intl's router.replace: works
    // reliably across LTR↔RTL flips (the soft-nav was occasionally
    // failing to fire on /he → /fr/en), and replays the KessemLoader
    // which the user expects as a fresh-start signal.
    const target = `/${next}${pathname === "/" ? "" : pathname}`;
    window.location.assign(target);
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="ghost"
            size="sm"
            className="h-11 gap-1.5 px-3 text-xs font-medium uppercase tracking-wider active:scale-95"
            // Accessible name must start with / contain the visible label
            // (`FR` / `EN` / `HE`) so voice-control users speaking what
            // they read on screen actually hits the button (axe-core
            // `label-content-name-mismatch`).
            aria-label={`${currentLocale.toUpperCase()} — ${t("language")}`}
          />
        }
      >
        <Globe className="size-3.5 opacity-70" aria-hidden />
        <span className="hidden sm:inline">{currentLocale}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-44">
        <div className="px-2 py-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          {t("language")}
        </div>
        <DropdownMenuSeparator />
        {routing.locales.map((l) => {
          const isActive = l === currentLocale;
          const meta = localeMeta[l];
          return (
            <DropdownMenuItem
              key={l}
              onClick={() => setLocale(l)}
              className="gap-2.5"
              dir={meta.dir}
            >
              <span aria-hidden className="text-base leading-none">
                {meta.flag}
              </span>
              <span className="flex-1">{meta.nativeLabel}</span>
              {isActive && (
                <Check className="size-3.5 text-primary" aria-hidden />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
